import { DateHelper, Model } from "@bryntum/schedulerpro";
import { HasProjectMixin } from "./mixins/HasProjectMixin.jsx";
import {
  ALERT_TYPE_NAMES, ALERT_TYPES,
  GRID_FIELDS_TO_OPEN_EDITOR,
  NOTIFICATION_STATUS,
  REMINDER_STATE,
  ReminderTypes
} from "../../other/constants/OtherKeys.jsx";
import { STORE_KEYS } from "../../other/constants/StoreKeys.jsx";
import { markAsyncTaskAsSeen } from "../../network/network.jsx";
import { showToast } from "../../other/utils/utils.jsx";

export const RECURRING_INTERVALS = [
  { id: 0, defaultCount: 0, value: "Never" },
  { id: 1, defaultCount: 30, value: "Daily" },
  { id: 2, defaultCount: 52, value: "Weekly" },
  { id: 3, defaultCount: 12, value: "Monthly" },
  { id: 4, defaultCount: 3, value: "Yearly" }
];

export class Reminder extends HasProjectMixin(Model) {
  static get $name() {
    return "Reminder";
  }

  static relations = {
    taskRecord: {
      foreignKey: "taskId",
      foreignStore: STORE_KEYS.taskStore,
      relatedCollectionName: "alerts",
    },
    resourceRecord: {
      foreignKey: "resourceId",
      foreignStore: STORE_KEYS.resourceStore,
      relatedCollectionName: "alerts",
    },
    clientRecord: {
      foreignKey: "clientId",
      foreignStore: STORE_KEYS.clientStore,
      relatedCollectionName: "alerts",
    },
    creatorBoardUserRecord: {
      foreignKey: "boardUserId",
      foreignStore: STORE_KEYS.boardUserStore,
      relatedCollectionName: "boardUsers",
    },
  }

  static get fields() {
    return [
      {name: "boardId", dataSource: "board", type: "number"},
      {name: "clientId", dataSource: "client", type: "number"},
      {name: "resourceId", dataSource: "resource", type: "number"},
      {name: "taskId", dataSource: "task", type: "number"},
      {name: "alertTag", dataSource: "alert_tag"},
      {name: "description", dataSource: "description"},
      {name: "title", dataSource: "title"},
      {name: "alertDate", dataSource: "alert_date", type: "date"},
      {name: "archived", dataSource: "archived"},
      {name: "order", type: "number"},
      {name: "remaining", persist: false},
      {name: "name", persist: false}, // this will the name of entity
      {name: "entityType", dataSource: "type", type: "number"}, // one of type id [Resource => 1, Client => 2, and Task => 3]
      {name: "alertType", dataSource: "alert_type"},  // one of type [Reminders, Weather, Emergency, Budget, Deadlines, Schedule, and Calendar]
      {name: "_alertDate", type: "date", persist: false},
      {name: "_alertTime", type: "date", persist: false},
      // reminder states
      // Bug: If type array is assigned then it triggers a replay effect
      // Todo: Downgrade to version: 6.0.3 to check whether problem exists there as well
      {name: "boardUserSeen", dataSource: "board_users_seen", defaultValue: []},
      {name: "status", type: "string", persist: false}, // one of the types ["Pending", "Sent", and "Fail"]
      {name: "reminderState", persist: false},
      {name: "recurringInterval", dataSource: "recurring_interval" },
      {name: "repeatCount", dataSource: "repeat_count" },
      {name: "boardUserId", dataSource: "creator", type: "number"},
      {name: "releasedCount", dataSource: "repeated_count", defaultValue: 0, type: "number"},
      {name: "dataType", dataSource: "data_type", type: "number"},
      {name: "asyncTask", dataSource: "async_task", type: "number"},
    ];
  }

  static swivuGetFieldOriginalSourceName(fieldName) {
    const fieldsMaps = {
      "_alertDate": "alert_date",
      "_alertTime": "alert_date",
    };
    return fieldsMaps[fieldName] || super.swivuGetFieldOriginalSourceName?.(fieldName);
  }

  get reminderRecipientValues() {
    return this.alertRecipients?.map((recipient) => recipient.userRecord?.name || "N/A") || [];
  }

  get _alertDate() {
    try {
      const lastAlertDate = this.get("alertDate");
      if (DateHelper.isValidDate(lastAlertDate)) {
        return DateHelper.startOf(lastAlertDate, "day");
      }
    } catch (err) {
      console.log("_alertDate something went wrong", err);
    }
    return null;
  }

  set _alertDate(newAlertDate) {
    const lastAlertDate = this.get("alertDate");
    if (DateHelper.isValidDate(lastAlertDate)) {
      newAlertDate.setHours(lastAlertDate.getHours(), lastAlertDate.getMinutes(), 0, 0);
    }
    this.set("alertDate", newAlertDate);
  }

  get _alertTime() {
    const lastAlertDate = this.get("alertDate");
    if (DateHelper.isValidDate(lastAlertDate)) {
      const newDate = new Date();
      newDate.setHours(lastAlertDate.getHours(), lastAlertDate.getMinutes(), 0, 0);
      return newDate;
    }
    return null;
  }

  set _alertTime(newAlertDate) {
    if (this.get("alertDate")) {
      // we need it to clone date otherwise changes in original source data cause date to not persist
      const lastAlertDate = DateHelper.clone(this.get("alertDate"));
      if (DateHelper.isValidDate(lastAlertDate)) {
        lastAlertDate.setHours(newAlertDate.getHours(), newAlertDate.getMinutes(), 0, 0);
        newAlertDate = lastAlertDate;
      }
    }
    this.set("alertDate", newAlertDate);
  }

  get [GRID_FIELDS_TO_OPEN_EDITOR.ENTITY_NAME]() {
    return this.entity?.name || this.title || "";
  }

  // Todo: fix updating entity name doesn't update it on main alert grid, may be we need to create a non persist local field
  get entity() {
    if (!this.project) return null; // don't know but when deleting the reminder we get project to be undefined
    if (this.entityType === ReminderTypes.resource) return this.project.resourceStore.getById(this.resourceId);
    if (this.entityType === ReminderTypes.client) return this.project.clientsStore.getById(this.clientId);
    if (this.entityType === ReminderTypes.task) return this.project.extraTaskStore.getById(this.taskId);
    return null;
  }

  get asEntityType() {
    if (this.entityType === ReminderTypes.resource) return "Resource";
    else if (this.entityType === ReminderTypes.task) return "Task";
    else if (this.entityType === ReminderTypes.client) return "Client";
    else if (this.entityType === ReminderTypes.boardAlert) return "Board User";
    return "";
  }

  get recipients() {
    if (this.project) { // when grouping is applied the project received as null
      return this.project.reminderRecipientStore.query((reminderRecipient) => reminderRecipient.reminderId === this.id);
    }
    return [];
  }

  // reminder triggers
  get remaining() {
    if (this.alertDate) return DateHelper.diff(new Date(), new Date(this.alertDate), "day", false) + 1;
    return 0;
  }

  get isSeen() {
    if (this.project) {
      const currentBoardUser = this.project.boardUsersStore.swivuCurrentUser;
      const boardUser = this.project.boardUsersStore.find((boardUserRecord) => boardUserRecord.userId === currentBoardUser.id);
      return this.boardUserSeen.includes(boardUser.id);
    }
    return false;
  }

  get reminderState() {
    // Constraint: Whenever a reminder/alert is released, server resets the `boardUserSeen` state
    const isSeen = this.isSeen;
    const alertType = this.get("alertType");
    const alertStatus = this.get("status");
    // This logic runs for Reminder, Reminder Added and Reminder Updated. while it should only run on Reminder
    // but since while adding a reminder we don't have any specific way to differentiate
    // between the two, other than utilizing the alert_tag field which is not available at the point of adding the reminder
    if (alertType === ALERT_TYPES.REMINDER) {
      if (this.isRecurringReminder) {
        const releasedCount = this.get("releasedCount");
        return this.getRecurringReminderState(releasedCount, isSeen);
      } else {
        return this.getNonRecurringReminderState(alertStatus, isSeen)
      }
    } else {
      return this.getAlertState(alertStatus, isSeen);
    }
  }

  getRecurringReminderState(releasedCount, isSeen) {
    return releasedCount ? (isSeen ? REMINDER_STATE.SEEN : REMINDER_STATE.NOT_SEEN) : REMINDER_STATE.NOT_SENT;
  }

  getNonRecurringReminderState(alertStatus, isSeen) {
    return alertStatus === NOTIFICATION_STATUS.SENT
      ? (isSeen ? REMINDER_STATE.SEEN : REMINDER_STATE.NOT_SEEN)
      : REMINDER_STATE.NOT_SENT;
  }

  getAlertState(alertStatus, isSeen) {
    if (alertStatus === NOTIFICATION_STATUS.FAILED) return REMINDER_STATE.FAILED;
    if (alertStatus === NOTIFICATION_STATUS.SENT) return isSeen ? REMINDER_STATE.SEEN: REMINDER_STATE.NOT_SEEN;
    return REMINDER_STATE.NOT_SENT;
  }

  get isRecurringReminder() {
    return Boolean(this.get("recurringInterval"))
  }

  get alertTypeName() {
    return ALERT_TYPE_NAMES[this.get("alertType")];
  }

  get isIAmOneOfRecipient() {
    if (this.project) {
      const currentBoardUser = this.project.boardUsersStore.swivuCurrentUser;
      const boardUser = this.project.boardUsersStore.find((boardUserRecord) => boardUserRecord.userId === currentBoardUser.id);
      return this.recipients.find((recipient) => recipient.boardUser.id === boardUser.id);
    }
    return false;
  }

  /**
  * @param {"toggle" | "markSeen" | "markUnseen"} action
  */
  updateSeenStatus(action = "toggle") {
    if ((this.alertType === ALERT_TYPES.AsyncTask) || (this.status === NOTIFICATION_STATUS.SENT || this.releasedCount)) {
      const currentBoardUser = this.project.boardUsersStore.swivuCurrentUser;
      const boardUser = this.project.boardUsersStore.find((boardUserRecord) => boardUserRecord.userId === currentBoardUser.id);
      const seenIndex = this.boardUserSeen.indexOf(boardUser.id);
      let shouldMarkAsSeen = false;
      const newBoardUserSeen = [...this.boardUserSeen];
      if (action === "markSeen" && seenIndex === -1) {
        newBoardUserSeen.push(boardUser.id);
        shouldMarkAsSeen = true;
      } else if (action === 'markUnseen' && seenIndex !== -1) {
        newBoardUserSeen.splice(seenIndex, 1);
        shouldMarkAsSeen = false;
      } else if (action === "toggle") {
        if (seenIndex !== -1) {
          newBoardUserSeen.splice(seenIndex, 1);
          shouldMarkAsSeen = false;
        } else {
          newBoardUserSeen.push(boardUser.id);
          shouldMarkAsSeen = true;
        }
      }
      this.set({ boardUserSeen: newBoardUserSeen, reminderState: null });

      // Call API for async tasks
      if (this.alertType === ALERT_TYPES.AsyncTask && this.description !== "Pending") {
        markAsyncTaskAsSeen(this.asyncTask, shouldMarkAsSeen).catch((error) => {
          showToast({msg: error || "Something went wrong"});
        })
      }
    }
  }

  get recurringIntervalValue() {
    let recurringIntervalText = "";
    const recurringInterval = this.get("recurringInterval");
    if (!isNaN(recurringInterval)) {
      recurringIntervalText = RECURRING_INTERVALS.find((interval) => interval.id === recurringInterval)?.value ?? "";
    }
    return recurringIntervalText;
  }
}

Reminder.exposeProperties();
